/**
 * Runtime Status visuals for the approved 2026-09-06 product palette.
 *
 * Component identity/geometry still comes from the final Figma Status package, but
 * the visible status colors and frame copy are superseded by the current locked
 * product decision. The same color pair drives both the Status choice-circle
 * preview and the number Status Frame.
 *
 * Product color rule:
 *   fill[0] = dark center behind the frame label
 *   fill[1] = brighter color mirrored to both outer edges
 *
 * AutoClan keeps a fixed palette but its frame label is dynamic and comes from the
 * registered clan name supplied in VehicleStatus.title. SuggestOwn is action-only
 * and never resolves to a number frame.
 */
export type FinalStatusVisual = {
  figmaComponent: string
  fill: readonly [string, string]
  frameLabel?: string
}

const componentVisuals = {
  AutoLady: { figmaComponent: "Plate Status / AutoLady", fill: ["#8723C4", "#FF2E9B"], frameLabel: "КОРОЛЕВА ДОРОГ" },
  Taxi: { figmaComponent: "Plate Status / Taxi", fill: ["#D66A00", "#FFE000"], frameLabel: "ЛОВЕЦ ЗАКАЗОВ" },
  NightDriver: { figmaComponent: "Plate Status / NightDriver", fill: ["#04060B", "#274C8E"], frameLabel: "ПРИЗРАЧНЫЙ ГОНЩИК" },
  Dacha: { figmaComponent: "Plate Status / Dacha", fill: ["#49351A", "#69D13F"], frameLabel: "КОРОЛЬ ШЕСТИ СОТОК" },
  NewDriver: { figmaComponent: "Plate Status / NewDriver", fill: ["#1C1C1C", "#FFD600"], frameLabel: "УКРОТИТЕЛЬ СЦЕПЛЕНИЯ" },
  Electric: { figmaComponent: "Plate Status / Electric", fill: ["#0A56A8", "#3FD3F3"], frameLabel: "МЕГАВОЛЬТ" },
  Hybrid: { figmaComponent: "Plate Status / Hybrid", fill: ["#17633E", "#52DCC7"], frameLabel: "ЭКОДЖЕДАЙ" },
  Gas: { figmaComponent: "Plate Status / Gas", fill: ["#145DA0", "#FFE45C"], frameLabel: "РЕАКТИВНЫЙ ФЕНИКС" },
  Kids: { figmaComponent: "Plate Status / Kids", fill: ["#C92F8E", "#5EDFF2"], frameLabel: "БЭБИ-ШАТТЛ" },
  Family: { figmaComponent: "Plate Status / Family", fill: ["#633746", "#EFA24B"], frameLabel: "СЕМЕЙНЫЙ ЭКСПРЕСС" },
  Traveler: { figmaComponent: "Plate Status / Traveler", fill: ["#302457", "#8D7CFF"], frameLabel: "БЕСПЕЧНЫЙ СТРАННИК" },
  Trucker: { figmaComponent: "Plate Status / Trucker", fill: ["#20262D", "#7E95A8"], frameLabel: "ВЕТЕРАН БОЛЬШИХ ДОРОГ" },
  Offroad: { figmaComponent: "Plate Status / Offroad", fill: ["#292D24", "#9B8B55"], frameLabel: "НЕУДЕРЖИМЫЙ ПРОХОДИМЕЦ" },
  Speed: { figmaComponent: "Plate Status / Speed", fill: ["#26320B", "#BFFF00"], frameLabel: "АДРЕНАЛИНОВЫЙ СОМЕЛЬЕ" },
  Sportcar: { figmaComponent: "Plate Status / Sportcar", fill: ["#690D12", "#FF2800"], frameLabel: "ОКТАНОВЫЙ ХИЩНИК" },
  Retro: { figmaComponent: "Plate Status / Retro", fill: ["#47212B", "#C08A58"], frameLabel: "АРИСТОКРАТ" },
  Tuning: { figmaComponent: "Plate Status / Tuning", fill: ["#16181C", "#FF6602"], frameLabel: "КАСТОМНЫЙ ЭКЗЕМПЛЯР" },
  Neighbor: { figmaComponent: "Plate Status / Neighbor", fill: ["#0A0C0F", "#42474D"], frameLabel: "МЕСТНЫЙ АВТОРИТЕТ" },
  GarageClub: { figmaComponent: "Plate Status / GarageClub", fill: ["#303233", "#6F765B"], frameLabel: "КООПЕРАТИВНОЕ БРАТСТВО" },
  Courier: { figmaComponent: "Plate Status / Courier", fill: ["#123E52", "#32C7D9"], frameLabel: "КУРЬЕР ОСОБОГО НАЗНАЧЕНИЯ" },
  WorkCar: { figmaComponent: "Plate Status / WorkCar", fill: ["#28343C", "#71899A"], frameLabel: "ШТАТНЫЙ АГЕНТ" },
  Cargo: { figmaComponent: "Plate Status / Cargo", fill: ["#1B221C", "#33A84B"], frameLabel: "ВЕСОМЫЙ ПЕРСОНАЖ" },
  OwnStyle: { figmaComponent: "Plate Status / OwnStyle", fill: ["#8723C4", "#3FD3F3"], frameLabel: "МАСТЕР ЭПАТАЖА" },
  AutoClan: { figmaComponent: "Plate Status / AutoClan", fill: ["#011920", "#024750"] },
  SuggestOwn: { figmaComponent: "Plate Status / SuggestOwn", fill: ["#021525", "#021525"] },
} as const satisfies Record<string, FinalStatusVisual>

export const FINAL_STATUS_VISUALS: Record<string, FinalStatusVisual> = {
  "auto-lady": componentVisuals.AutoLady, auto_lady: componentVisuals.AutoLady, autolady: componentVisuals.AutoLady,
  taxi: componentVisuals.Taxi,
  "night-driver": componentVisuals.NightDriver, night_driver: componentVisuals.NightDriver,
  dacha: componentVisuals.Dacha,
  "new-driver": componentVisuals.NewDriver, new_driver: componentVisuals.NewDriver,
  electric: componentVisuals.Electric, hybrid: componentVisuals.Hybrid, gas: componentVisuals.Gas,
  kids: componentVisuals.Kids, family: componentVisuals.Family, traveler: componentVisuals.Traveler,
  trucker: componentVisuals.Trucker, offroad: componentVisuals.Offroad, speed: componentVisuals.Speed,
  sportscar: componentVisuals.Sportcar, sportcar: componentVisuals.Sportcar, retro: componentVisuals.Retro, tuning: componentVisuals.Tuning,
  neighbor: componentVisuals.Neighbor,
  "garage-club": componentVisuals.GarageClub, garage_club: componentVisuals.GarageClub,
  courier: componentVisuals.Courier,
  "work-car": componentVisuals.WorkCar, work_car: componentVisuals.WorkCar,
  cargo: componentVisuals.Cargo,
  "own-style": componentVisuals.OwnStyle, own_style: componentVisuals.OwnStyle,
  autoclan: componentVisuals.AutoClan, clan_member: componentVisuals.AutoClan,
  "suggest-own": componentVisuals.SuggestOwn, suggest_own: componentVisuals.SuggestOwn,
}
